import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const TaskDetails = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // State for tasks and loading
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for filters
  const [filters, setFilters] = useState({
    taskNumber: '',
    siteName: '',
    dateFrom: '',
    dateTo: '',
    status: '', // '' for all, 'completed', 'pending'
  });

  // State for filtered results
  const [filteredTasks, setFilteredTasks] = useState([]);

  // Fetch tasks on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch tasks
        const tasksResponse = await axios.get(`${apiUrl}/api/tasks`);
        const responseData = tasksResponse.data;
        
        // Handle different API response structures
        let tasksData = [];
        if (Array.isArray(responseData)) {
          tasksData = responseData;
        } else if (responseData?.data) {
          if (Array.isArray(responseData.data)) {
            tasksData = responseData.data;
          } else if (responseData.data?.data && Array.isArray(responseData.data.data)) {
            tasksData = responseData.data.data;
          }
        }
        
        setTasks(tasksData);
        setFilteredTasks(tasksData);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load task data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  // Apply filters whenever they change or tasks change
  useEffect(() => {
    applyFilters();
  }, [filters, tasks]);

  // Helper function to get site name (customer name) from nested object
  const getSiteName = (task) => {
    return task.customer?.name || task.site_name || '';
  };

  // Helper function to normalize status
  const getTaskStatus = (task) => {
    const status = task.status?.toLowerCase() || task.task_status?.toLowerCase() || 'pending';
    // 'undone' should map to 'Pending', only 'done' and 'completed' map to 'Completed'
    return status === 'done' || status === 'completed' ? 'Completed' : 'Pending';
  };

  // Helper function to get task name
  const getTaskName = (task) => {
    return task.task || task.title || task.task_name || '';
  };

  // Helper function to get task description
  const getTaskDescription = (task) => {
    return task.description || task.task_description || '';
  };

  // Helper function to get task date (prefer date_from, fallback to created_at)
  const getTaskDate = (task) => {
    return task.date_from || task.task_date || task.created_at || '';
  };

  // Helper function to get date from
  const getDateFrom = (task) => {
    return task.date_from || '';
  };

  // Helper function to get date to
  const getDateTo = (task) => {
    return task.date_to || '';
  };

  // Apply filters to tasks
  const applyFilters = () => {
    let filtered = [...tasks];

    // Filter by task number
    if (filters.taskNumber) {
      filtered = filtered.filter((task) =>
        (task.task_number || task.id?.toString() || '')
          .toLowerCase()
          .includes(filters.taskNumber.toLowerCase())
      );
    }

    // Filter by site name
    if (filters.siteName) {
      filtered = filtered.filter((task) =>
        getSiteName(task)
          .toLowerCase()
          .includes(filters.siteName.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter((task) => {
        const taskDate = new Date(getTaskDate(task));
        const fromDate = new Date(filters.dateFrom);
        return taskDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter((task) => {
        const taskDate = new Date(getTaskDate(task));
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        return taskDate <= toDate;
      });
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter((task) => {
        const taskStatus = getTaskStatus(task);
        return taskStatus === filters.status;
      });
    }

    setFilteredTasks(filtered);
  };

  // Handle filter input change
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      taskNumber: '',
      siteName: '',
      dateFrom: '',
      dateTo: '',
      status: '',
    });
  };

  // Export table to PDF
  const exportToPDF = async () => {
    try {
      const pdfDocument = React.createElement(TaskDetailsPDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TaskDetails_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // PDF Styles
  const pdfStyles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Helvetica',
    },
    title: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 15,
      color: '#0891b2',
    },
    subtitle: {
      fontSize: 10,
      color: '#666',
      marginBottom: 20,
    },
    table: {
      width: '100%',
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#f3f4f6',
      fontWeight: 'bold',
      fontSize: 9,
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 6,
    },
    tableRow: {
      flexDirection: 'row',
      fontSize: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 6,
    },
    col1: {
      width: '10%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col2: {
      width: '14%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col3: {
      width: '14%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col4: {
      width: '10%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col5: {
      width: '10%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col6: {
      width: '14%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col7: {
      width: '8%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    footerText: {
      fontSize: 9,
      color: '#666',
      marginTop: 10,
    },
  });

  // PDF Document Component
  const TaskDetailsPDFDocument = () => {
    const hasDateFilter = filters.dateFrom || filters.dateTo;
    const dateRangeText = hasDateFilter
      ? `${filters.dateFrom ? formatDate(filters.dateFrom) : 'Start'} - ${filters.dateTo ? formatDate(filters.dateTo) : 'End'}`
      : '';

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <Text style={pdfStyles.title}>Task Details Report</Text>
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>
              Generated on {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Text>
            {hasDateFilter && (
              <Text style={{ fontSize: 10, color: '#0891b2', fontWeight: 'bold', marginTop: 5 }}>
                Period: {dateRangeText}
              </Text>
            )}
          </View>

          <View style={pdfStyles.table}>
            {/* Table Header */}
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.col1}>Task #</Text>
              <Text style={pdfStyles.col2}>Task Name</Text>
              <Text style={pdfStyles.col3}>Site Name</Text>
              <Text style={pdfStyles.col4}>Date From</Text>
              <Text style={pdfStyles.col5}>Date To</Text>
              <Text style={pdfStyles.col6}>Description</Text>
              <Text style={pdfStyles.col7}>Status</Text>
            </View>

            {/* Table Rows */}
            {filteredTasks.map((task) => (
              <View key={task.id} style={pdfStyles.tableRow}>
                <Text style={pdfStyles.col1}>{task.task_number || task.id}</Text>
                <Text style={pdfStyles.col2}>{getTaskName(task)}</Text>
                <Text style={pdfStyles.col3}>{getSiteName(task)}</Text>
                <Text style={pdfStyles.col4}>{formatDate(getDateFrom(task))}</Text>
                <Text style={pdfStyles.col5}>{formatDate(getDateTo(task))}</Text>
                <Text style={pdfStyles.col6}>{getTaskDescription(task).substring(0, 35)}</Text>
                <Text style={pdfStyles.col7}>{getTaskStatus(task)}</Text>
              </View>
            ))}
          </View>

          <Text style={pdfStyles.footerText}>
            Showing {filteredTasks.length} of {tasks.length} tasks
          </Text>
        </Page>
      </Document>
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading task data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Task Details</h1>
          <p className="text-gray-600 mt-2">
            View and search all tasks
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
            {/* Task Number Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task #
              </label>
              <input
                type="text"
                placeholder="Search task..."
                value={filters.taskNumber}
                onChange={(e) =>
                  handleFilterChange('taskNumber', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Site Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Name
              </label>
              <input
                type="text"
                placeholder="Search site..."
                value={filters.siteName}
                onChange={(e) =>
                  handleFilterChange('siteName', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="Completed">Completed</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* Spacer */}
            <div></div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset Filters
          </button>

          {/* Export Button */}
          <button
            onClick={exportToPDF}
            disabled={filteredTasks.length === 0}
            className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Export to PDF
          </button>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {filteredTasks.length > 0 ? (
            <>
              <table className="w-full">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Task #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Task Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Site Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Date From
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Date To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTasks.map((task) => (
                    <tr
                      key={task.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-blue-600">
                          {task.task_number || task.id}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTaskName(task)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getSiteName(task)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(getDateFrom(task))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(getDateTo(task))}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {getTaskDescription(task).substring(0, 60)}
                        {getTaskDescription(task).length > 60 ? '...' : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            getTaskStatus(task) === 'Completed'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {getTaskStatus(task)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No tasks found matching your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetails;
